import { useGmbImplementationOverview } from '../../hooks/useGmbImplementation';
import TutorialCard from '../TutorialCard';

interface ImplementationOverviewProps {
  onNavigate: (section: string) => void;
  onTooltipShow: (title: string, content: string) => void;
}

const colorMap: Record<string, { bg: string; text: string; iconBg: string }> = {
  blue: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', iconBg: 'bg-blue-100 dark:bg-blue-800/40' },
  green: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', iconBg: 'bg-green-100 dark:bg-green-800/40' },
  purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', iconBg: 'bg-purple-100 dark:bg-purple-800/40' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', iconBg: 'bg-orange-100 dark:bg-orange-800/40' },
};

const pillColors: Record<string, string> = {
  done: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  'at-risk': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  'not-started': 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  'needs-more': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
};

export default function ImplementationOverview({ onNavigate, onTooltipShow }: ImplementationOverviewProps) {
  const { data: overviewData, isLoading, error, refetch } = useGmbImplementationOverview();

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">Failed to load implementation overview</h3>
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error.message}</p>
        <button onClick={() => refetch()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-2"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4"></div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const stats = overviewData?.data?.stats || [];
  const snapshots = overviewData?.data?.snapshots || [];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat: any) => {
          const c = colorMap[stat.color] || colorMap.blue;
          return (
            <div key={stat.label} className={`${c.bg} rounded-lg p-5`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`${c.iconBg} ${c.text} w-8 h-8 rounded-md flex items-center justify-center`}>
                  <i className={`${stat.icon} text-sm`}></i>
                </div>
                <span className={`text-sm font-medium ${c.text}`}>{stat.label}</span>
              </div>
              <div className={`text-2xl font-bold ${c.text}`}>{stat.value}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.change}</div>
            </div>
          );
        })}
      </div>

      {/* Tutorial Banner */}
      <TutorialCard
        title="How to Use Implementation Tools"
        description="Learn how to manage photos, posts, and reviews"
        duration="2:30 min"
        variant="dark"
      />

      {/* Subsection Snapshot Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Implementation Snapshot</h3>
          <button
            onClick={() => onTooltipShow('Implementation Snapshot', 'Quick overview of your implementation progress across photos, posts, and Q&A. Click any card to navigate to that section.')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="More info"
          >
            <i className="ri-information-line text-lg"></i>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {snapshots.map((snap: any) => (
            <button
              key={snap.id}
              onClick={() => onNavigate(snap.id)}
              className={`text-left rounded-lg border-l-4 border-${snap.borderColor}-500 bg-white dark:bg-gray-800 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700`}
            >
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">{snap.title}</h4>

              {snap.gridItems && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {snap.gridItems.map((item: any) => (
                    <div key={item.label} className="flex items-center gap-2 text-xs">
                      <i
                        className={`${
                          item.status === 'done' ? 'ri-checkbox-circle-fill text-green-500' : 'ri-close-circle-fill text-red-500'
                        } text-sm`}
                      ></i>
                      <span className="text-gray-700 dark:text-gray-300">{item.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {snap.weekDays && (
                <div className="flex items-center gap-2 mb-3">
                  {snap.weekDays.map((wd: any) => {
                    const dotColor =
                      wd.status === 'posted'
                        ? 'bg-green-400'
                        : wd.status === 'scheduled'
                          ? 'bg-orange-400'
                          : 'bg-gray-200 dark:bg-gray-600';
                    return (
                      <div key={wd.day} className="flex flex-col items-center gap-1">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{wd.day}</span>
                        <div className={`w-3 h-3 rounded-full ${dotColor}`}></div>
                      </div>
                    );
                  })}
                </div>
              )}

              {snap.checklist && (
                <div className="space-y-1 mb-3">
                  {snap.alertPill && (
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-medium">
                        {snap.alertPill}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-xs">
                    {snap.checklist.map((ci: any) => (
                      <div key={ci.label} className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${ci.color === 'green' ? 'bg-green-400' : ci.color === 'orange' ? 'bg-orange-400' : 'bg-red-400'}`}></div>
                        <span className="text-gray-600 dark:text-gray-400">{ci.count} {ci.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500 dark:text-gray-400">{snap.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
